import { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { LogOut, Menu, User, X } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { EASE } from "@/components/Motion";

const LINKS = [
  { to: "/about", label: "About", id: "nav-about-link" },
  { to: "/services", label: "Services", id: "nav-services-link" },
  { to: "/notices", label: "Notices", id: "nav-notices-link" },
  { to: "/contact", label: "Contact", id: "nav-contact-link" },
];

const Logo = ({ onClick }) => (
  <Link to="/" data-testid="nav-logo-link" onClick={onClick} className="flex items-center gap-3">
    <span
      className="flex h-9 w-9 items-center justify-center rounded-md bg-blue-900 font-display text-sm font-bold text-white"
      aria-hidden="true"
    >
      N
    </span>
    <span className="leading-tight">
      <span className="block font-display text-base font-bold tracking-tight text-slate-900">
        NUC Service
      </span>
      <span className="block font-mono text-[9px] uppercase tracking-[0.15em] text-slate-500">
        National Union Corporation
      </span>
    </span>
  </Link>
);

export default function Navbar() {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    setOpen(false);
    navigate("/");
  };

  return (
    <>
      <header className="sticky top-0 z-50 border-b border-slate-200 bg-white/80 backdrop-blur-xl">
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
          <Logo />
          <nav className="hidden items-center gap-7 md:flex" aria-label="Primary">
            {LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={l.id}
                className={({ isActive }) =>
                  `text-sm font-medium transition-colors duration-200 ${
                    isActive ? "text-blue-900" : "text-slate-600 hover:text-slate-900"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
          </nav>
          <div className="hidden items-center gap-4 md:flex">
            {user ? (
              <>
                <Link
                  to={user.role === "admin" ? "/admin" : "/account"}
                  data-testid="nav-account-link"
                  className="flex items-center gap-2 text-sm font-medium text-slate-700 transition-colors duration-200 hover:text-blue-900"
                >
                  <User className="h-4 w-4" />
                  {user.name?.split(" ")[0] || "Account"}
                </Link>
                <button
                  onClick={handleLogout}
                  data-testid="logout-button"
                  aria-label="Log out"
                  className="text-slate-400 transition-colors duration-200 hover:text-red-600"
                >
                  <LogOut className="h-4 w-4" />
                </button>
              </>
            ) : (
              <Link
                to="/login"
                data-testid="nav-login-link"
                className="text-sm font-medium text-slate-600 transition-colors duration-200 hover:text-slate-900"
              >
                Login
              </Link>
            )}
            <Link
              to="/book"
              data-testid="nav-book-button"
              className="rounded-md bg-blue-900 px-5 py-2.5 text-sm font-medium text-white transition-colors duration-200 hover:bg-blue-800"
            >
              Book a Service
            </Link>
          </div>
          <button
            className="text-slate-900 md:hidden"
            data-testid="mobile-menu-button"
            onClick={() => setOpen(true)}
            aria-label="Open menu"
          >
            <Menu className="h-6 w-6" />
          </button>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="fixed inset-0 z-[60] flex flex-col bg-white"
            data-testid="mobile-menu"
          >
            <div className="flex h-16 items-center justify-between border-b border-slate-200 px-4">
              <Logo onClick={() => setOpen(false)} />
              <button
                onClick={() => setOpen(false)}
                data-testid="mobile-menu-close-button"
                aria-label="Close menu"
                className="text-slate-900"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
            <nav className="flex flex-1 flex-col gap-1 overflow-y-auto px-4 py-6" aria-label="Mobile">
              {[{ to: "/", label: "Home", id: "nav-home-link" }, ...LINKS].map((l, i) => (
                <motion.div
                  key={l.to}
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: 0.05 + i * 0.05, ease: EASE }}
                >
                  <NavLink
                    to={l.to}
                    data-testid={`mobile-${l.id}`}
                    onClick={() => setOpen(false)}
                    className={({ isActive }) =>
                      `block rounded-lg px-4 py-3.5 font-display text-xl font-bold tracking-tight ${
                        isActive ? "bg-blue-50 text-blue-900" : "text-slate-900"
                      }`
                    }
                  >
                    {l.label}
                  </NavLink>
                </motion.div>
              ))}
            </nav>
            <div className="flex flex-col gap-3 border-t border-slate-200 px-4 py-6">
              <Link
                to="/book"
                data-testid="mobile-nav-book-button"
                onClick={() => setOpen(false)}
                className="rounded-md bg-blue-900 px-5 py-3 text-center text-sm font-medium text-white"
              >
                Book a Service
              </Link>
              {user ? (
                <>
                  <Link
                    to={user.role === "admin" ? "/admin" : "/account"}
                    data-testid="mobile-nav-account-link"
                    onClick={() => setOpen(false)}
                    className="rounded-md border-2 border-slate-300 px-5 py-3 text-center text-sm font-medium text-slate-700"
                  >
                    My Account
                  </Link>
                  <button
                    onClick={handleLogout}
                    data-testid="mobile-logout-button"
                    className="text-sm font-medium text-red-600"
                  >
                    Log out
                  </button>
                </>
              ) : (
                <Link
                  to="/login"
                  data-testid="mobile-nav-login-link"
                  onClick={() => setOpen(false)}
                  className="rounded-md border-2 border-slate-300 px-5 py-3 text-center text-sm font-medium text-slate-700"
                >
                  Login
                </Link>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
